export default function PalmBeachTravelTimingSection() {
  const cards = [
    {
      icon: "ri-flight-takeoff-line",
      title: "PBI — Palm Beach International Airport",
      content: (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Palm Beach International (PBI) is one of the most efficient airports in the country for private and premium commercial travel. Unlike Fort Lauderdale or Miami, PBI offers a streamlined experience that matches the expectations of Palm Beach travelers — shorter security lines, closer parking, and direct international service to London, Toronto, and seasonal Caribbean destinations.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Many Palm Beach residents do not think about passport renewal until they are already booking their summer departure. European travel typically requires a passport valid for at least six months beyond the return date — a rule that catches seasonal residents who split their time between Palm Beach and European properties.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            The "six-month validity" requirement is not negotiable at check-in. Airlines will deny boarding even if your passport technically has two months remaining. For Palm Beach travelers who maintain dual residencies or travel frequently between Florida and European destinations, this is the most common reason an expedited passport becomes necessary.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#F6C344]">Passport tip:</strong> If you have a summer home in London, Paris, or Geneva and your passport expires within eight months of your planned return, start your renewal now. Standard processing will not meet a July departure if you apply in May.
          </p>
        </>
      ),
    },
    {
      icon: "ri-ship-line",
      title: "Yacht & Marina Departures",
      content: (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            The yacht culture in Palm Beach is not recreational — it is a lifestyle. Families affiliated with the Palm Beach Yacht Club, the Royal Poinciana Yacht Club, or private slips along the Intracoastal and Lake Worth Lagoon often plan Caribbean or Mediterranean itineraries that span weeks or months. These departures do not follow cruise ship schedules. They follow weather windows, crew contracts, and marina availability.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            A yacht departure to the Bahamas requires a valid passport for every person on board, including children. Trans-Atlantic passages to the Mediterranean — common for Palm Beach yachts heading to the summer season in the South of France or the Balearics — require passports with substantial remaining validity.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            The challenge: yacht itineraries are fluid. A family may plan a June departure, but weather, maintenance, or crew changes can shift that to May or July. If a passport issue is discovered during pre-departure preparation, standard processing will not accommodate the new timeline. This is why many Palm Beach yacht captains and family offices keep our contact information on file.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#F6C344]">Passport tip:</strong> If you are planning an extended yacht itinerary this season, verify every family member's passport expiration at least 90 days before the earliest possible departure window. Children under 16 have five-year passports — they expire faster than you expect.
          </p>
        </>
      ),
    },
    {
      icon: "ri-calendar-event-line",
      title: "Charity Gala & Social Season Calendar",
      content: (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Palm Beach's social season runs roughly from mid-December through Easter and is anchored by a series of high-profile charity galas, balls, and cultural events that draw attendees from across the country and abroad. The winter calendar includes the International Red Cross Ball, the Palm Beach Winter Gala, and numerous charity auctions that bring in European and South American donors and attendees.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Many Palm Beach families host or attend international guests during this season. When relatives from London, Paris, or São Paulo arrive for holiday visits, it is not uncommon for the hosting family to plan reciprocal trips abroad during the spring or summer. These trips are often planned around the social calendar — a return invitation to a Paris dinner, a villa rental in Tuscany timed to a friend's wedding, a yacht rendezvous in Monaco.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            The timing problem: these invitations often come with relatively short notice. A March gala connection might lead to a May departure. If your passport expired during the winter season and you did not notice, that May trip becomes impossible under standard processing timelines.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#F6C344]">Passport tip:</strong> Check your passport expiration during December social season preparation. It takes two minutes and can prevent a crisis when an international invitation arrives with a six-week lead time.
          </p>
        </>
      ),
    },
    {
      icon: "ri-medal-line",
      title: "Wellington Equestrian Season",
      content: (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            The Winter Equestrian Festival in Wellington is the largest and longest-running horse show in the world, running from January through March each year. It draws international competitors, trainers, grooms, and spectators from Europe, South America, and across North America. For Wellington residents and the equestrian families who base themselves here for the season, international travel is not optional — it is part of the sport.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Competitors who show at Wellington often travel to subsequent events at Spruce Meadows (Canada), Aachen (Germany), Hickstead (UK), or St. Gallen (Switzerland). These shows run on tight calendars with entry deadlines that require confirmed travel well in advance. A passport issue discovered in February — when the Wellington season is already underway — leaves almost no margin for standard processing before a March or April European departure.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            The equestrian community also includes a significant number of dual-nationality families. Children born to a U.S. parent and a European parent may hold two passports, but the U.S. passport is what they need for departure and return. When a young rider's U.S. passport expires mid-season, the entire competition schedule can be disrupted.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#F6C344]">Passport tip:</strong> Wellington trainers and barn managers: verify every minor rider's passport at least 60 days before the season's first international show commitment. A child's five-year passport can expire silently while everyone is focused on qualifying rounds.
          </p>
        </>
      ),
    },
  ];

  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Patterns</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Palm Beach Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Palm Beach travel patterns differ significantly from other Florida communities. Understanding these patterns helps avoid the passport emergencies that disrupt yacht itineraries, equestrian schedules, and European summer plans.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((card, i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                  <i className={`${card.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm leading-snug">{card.title}</h3>
              </div>
              <div>{card.content}</div>
            </div>
          ))}
        </div>

        {/* Additional callout */}
        <div className="mt-8 bg-[#1A365D] rounded-lg p-6 flex flex-col sm:flex-row items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
            <i className="ri-information-line text-[#1A365D] text-xl"></i>
          </div>
          <div>
            <p className="text-white font-bold text-sm mb-1">Private Aviation from PBI &amp; APF</p>
            <p className="text-white/70 text-sm leading-relaxed">
              Many Palm Beach residents travel via private aviation from PBI or nearby general aviation airports. Private departures to the Bahamas, Caribbean, or international destinations still require valid passports for every passenger. Charter operators and FBOs will verify passport validity before departure — there are no exceptions for private flights. If your passport expires during an extended trip, returning to the U.S. becomes significantly more complicated.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}